import { FORM_TEXT_KEYS, type FormTags, type FormTexts } from "@omnabase/shared";
import type { Draft, OpenProject } from "./formEdits";

/** Builder v2 (brief "The form rows"; red team finding 20, question 14): the words a closed row of the builder shows, so the person learns the
 *  form's state without opening every row. Pure, from the draft: they follow every unsaved change. */

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** the header's line: "Published · 6 fields", "Draft · 1 field", "Archived · 3 fields" (it follows the unsaved Published switch, question 3) */
export function statusLine(d: Pick<Draft, "published" | "items">, archived: boolean): string {
  return `${archived ? "Archived" : d.published ? "Published" : "Draft"} · ${count(d.items.length, "field")}`;
}

/** Tags: "No rules yet" with nothing set, otherwise the counts ("1 tag · 1 rule") */
export function tagsSummary(tags: FormTags): string {
  const n = tags.ticket.length + tags.customer.length;
  const parts = [n ? count(n, "tag") : "", tags.rules.length ? count(tags.rules.length, "rule") : ""].filter(Boolean);
  return parts.length ? parts.join(" · ") : "No rules yet";
}

/** Where submissions go: who decides the project, then Published or Draft ("Omnalingo · Published", "Picked by the person · Draft",
 *  "No project yet · Draft") */
export function whereSummary(d: Pick<Draft, "defaultProjectId" | "published" | "items">, projects: readonly OpenProject[]): string {
  const state = d.published ? "Published" : "Draft";
  if (d.items.some((f) => f.mapping === "project")) return `Picked by the person · ${state}`;
  if (!d.defaultProjectId) return `No project yet · ${state}`;
  return `${projects.find((p) => p.id === d.defaultProjectId)?.name ?? "A closed project"} · ${state}`;
}

/** Texts (the form editor round): "Default words" while nothing is typed, else how many of the five are the form's own */
export function textsSummary(texts: FormTexts): string {
  const own = FORM_TEXT_KEYS.filter((k) => texts[k].trim()).length;
  return own ? `${own} of 5 in your words` : "Default words";
}

/** Embed in your page: a short line */
export const EMBED_SUMMARY = "One line of code";
/** Share: the form's address as a path */
export const shareSummary = (slug: string): string => `/forms/${slug}`;
